import { Button, Icon, Modal, Typography } from '@globalmed/atlas-ui';
import { Trans, useTranslation } from 'react-i18next';
import styles from './KioskInactivityModal.module.css';
import { useCallback, useEffect, useState } from 'react';
import { useScreenInactivity } from '@/hooks/useScreenInactivity';

type TInactivityModalVariant = 'logout' | 'redirect';

type TProps = {
  variant: TInactivityModalVariant;
  onTimerEnd: () => void;
};

const INACTIVITY_TIMEOUT_SECONDS = 90;
const COUNTDOWN_SECONDS = 30;

const keysMap: Record<
  TInactivityModalVariant,
  { countdown: string; countdownSr: string; title: string }
> = {
  logout: {
    countdown: 'logging-out-in',
    countdownSr: 'logging-out-in-sr',
    title: 'you-will-be-logged-out-in-order-to-protect-your-privacy',
  },
  redirect: {
    countdown: 'redirect-in',
    countdownSr: 'redirect-in-sr',
    title:
      'you-will-be-redirected-to-the-landing-page-in-order-to-protect-your-privacy',
  },
};

/**
 * Displays a modal when the user has been inactive on the kiosk for a defined period of time.
 * The modal warns the user about an upcoming automatic logout or redirect and starts a countdown.
 * The modal is not rendered on the kiosk landing page when no user is logged in.
 * @param props - Component props
 * @param props.variant - Determines the modal displayed texts. Inactivity modal variants: 'logout' | 'redirect'.
 * @param props.onTimerEnd - Callback function executed when the countdown reaches zero.
 * @returns InactivityModal component.
 */
export const KioskInactivityModal = ({ variant, onTimerEnd }: TProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);
  // Snapshot of the countdown announced to screen readers when the modal
  // opens. Kept separate from `seconds` so the live region speaks once on
  // open instead of on every tick.
  const [announcedSeconds, setAnnouncedSeconds] = useState<number | null>(null);

  const onIdle = useCallback(() => {
    setSeconds(COUNTDOWN_SECONDS);
    setAnnouncedSeconds(COUNTDOWN_SECONDS);
    setOpen(true);
  }, []);

  useScreenInactivity(INACTIVITY_TIMEOUT_SECONDS, onIdle);

  useEffect(() => {
    if (!open) return;

    if (!seconds) {
      onTimerEnd();
      return;
    }

    const timer = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [open, seconds, onTimerEnd]);

  return (
    <Modal
      open={open}
      title={
        <div className={styles.titleContainer}>
          <Icon
            name="ClockAlert"
            color="var(--colors-warning600)"
            size="48"
            decorative
          />
          <Typography
            fontWeight={600}
            fontSize="20px"
            sx={{ lineHeight: '30px' }}
          >
            {t('are-you-still-there')}
          </Typography>
          <Typography
            color="var(--colors-secondary)"
            sx={{ lineHeight: '20px', textAlign: 'center' }}
          >
            {t(keysMap[variant].title)}
          </Typography>
        </div>
      }
      body={
        <div className={styles.bodyContainer}>
          <Typography
            fontWeight={500}
            fontSize="18px"
            sx={{ lineHeight: '28px' }}
          >
            <Trans
              i18nKey={keysMap[variant].countdown}
              values={{ seconds }}
              components={{
                seconds: (
                  <Typography
                    as="span"
                    fontWeight={600}
                    fontSize="20px"
                    sx={{ lineHeight: '30px' }}
                  />
                ),
              }}
            />
          </Typography>

          <Button
            text={t('yes-im-here')}
            onClick={() => {
              setAnnouncedSeconds(null);
              setOpen(false);
            }}
            wide
          />

          {announcedSeconds !== null && (
            <p role="status" aria-live="assertive" className={styles.srOnly}>
              {`${t('are-you-still-there')} ${t(keysMap[variant].countdownSr, {
                seconds: announcedSeconds,
              })}`}
            </p>
          )}
        </div>
      }
    />
  );
};
